import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { renderInvoicePdf } from '../downloadInvoice';
import { Invoice, InvoiceAcceptance } from '../types';
import {
    StyledDocumentWrapper,
    StyledMessage,
    StyledNavigator,
    StyledPageButton,
    StyledPageIndicator,
    StyledWrapper,
} from './styled';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface InvoiceDocumentViewProps {
    invoice: Invoice;
    acceptance?: InvoiceAcceptance | null;
    organizationId?: string;
    renderPdf?: (
        invoice: Invoice,
        acceptance: InvoiceAcceptance | null,
        agentId: string,
    ) => Promise<Blob>;
}

const paneContentBox = (node: HTMLElement) => {
    const styles = window.getComputedStyle(node);
    const padX = Number.parseFloat(styles.paddingLeft)
        + Number.parseFloat(styles.paddingRight);
    const padY = Number.parseFloat(styles.paddingTop)
        + Number.parseFloat(styles.paddingBottom);
    return {
        width: Math.max(0, node.clientWidth - padX),
        height: Math.max(0, node.clientHeight - padY),
    };
};

const InvoiceDocumentView = ({
    invoice,
    acceptance = null,
    organizationId = '',
    renderPdf = renderInvoicePdf,
}: InvoiceDocumentViewProps) => {
    const { agentId = '' } = useParams();
    const invoiceAgentId = organizationId || agentId;
    const [file, setFile] = useState<string | null>(null);
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pane, setPane] = useState({ width: 0, height: 0 });
    const [pageSize, setPageSize] = useState({ width: 612, height: 792 });
    const resizeObserverRef = useRef<ResizeObserver | undefined>();

    const pageWidth = useMemo(() => {
        if (pane.width <= 0 || pane.height <= 0) return 0;
        const ratio = pageSize.width / pageSize.height;
        return Math.floor(Math.min(pane.width, pane.height * ratio));
    }, [pageSize.height, pageSize.width, pane.height, pane.width]);

    const documentWrapperRef = useCallback((node: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = undefined;
        if (!node) return;
        const update = () => setPane(paneContentBox(node));
        update();
        const observer = new ResizeObserver(update);
        observer.observe(node);
        resizeObserverRef.current = observer;
    }, []);

    useEffect(() => () => {
        resizeObserverRef.current?.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';

        const render = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const blob = await renderPdf(invoice, acceptance, invoiceAgentId);
                objectUrl = URL.createObjectURL(blob);
                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }
                setFile(objectUrl);
            } catch (renderError) {
                if (cancelled) return;
                setFile(null);
                setError(
                    renderError instanceof Error
                        ? renderError.message
                        : 'Could not render invoice',
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        render();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [acceptance, invoice, invoiceAgentId, renderPdf]);

    return (
        <StyledWrapper>
            {isLoading ? <StyledMessage>Rendering PDF...</StyledMessage> : null}
            {error ? <StyledMessage>{error}</StyledMessage> : null}
            {!isLoading && !error && file ? (
                <>
                    <StyledDocumentWrapper ref={documentWrapperRef}>
                        <Document
                            file={file}
                            loading={<StyledMessage>Loading page...</StyledMessage>}
                            onLoadSuccess={({ numPages: nextNumPages }) => {
                                setNumPages(nextNumPages);
                                setCurrentPage((page) => Math.min(page, nextNumPages));
                            }}
                        >
                            {pageWidth > 0 ? (
                                <Page
                                    pageNumber={currentPage}
                                    width={pageWidth}
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                    onLoadSuccess={(page) => {
                                        const view = page.getViewport({ scale: 1 });
                                        setPageSize({
                                            width: view.width,
                                            height: view.height,
                                        });
                                    }}
                                />
                            ) : null}
                        </Document>
                    </StyledDocumentWrapper>
                    {numPages > 1 ? (
                        <StyledNavigator>
                            <StyledPageButton
                                type="button"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((page) => page - 1)}
                            >
                                ‹
                            </StyledPageButton>
                            <StyledPageIndicator>
                                {`${currentPage}/${numPages}`}
                            </StyledPageIndicator>
                            <StyledPageButton
                                type="button"
                                disabled={currentPage >= numPages}
                                onClick={() => setCurrentPage((page) => page + 1)}
                            >
                                ›
                            </StyledPageButton>
                        </StyledNavigator>
                    ) : null}
                </>
            ) : null}
        </StyledWrapper>
    );
};

export default InvoiceDocumentView;
